import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import Image, { ImageProps } from "../Image";

export type ButtonVariants =
  | "default"
  | "link"
  | "destructive"
  | "primary-outline"
  | "destructive-outline"
  | "outline"
  | "outline-secondary"
  | "secondary"
  | "ghost"
  | "ghost-primary"
  | "ghost-destructive"
  | "primary-accent"
  | "la-salle-green"
  | "butterfly-blue"
  | "hero"
  | "primary"
  | null
  | undefined;

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-[#1CACE0] text-white shadow-xs hover:bg-[#7BDCFF] active:bg-[#124792] disabled:bg-[#1CACE0]/25 disabled:opacity-100",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        "primary-outline": "border border-primary text-text-primary shadow-xs",
        "destructive-outline":
          "border border-destructive text-destructive bg-background shadow-xs hover:bg-destructive-accent",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        "outline-secondary":
          "border border-secondary font-medium text-secondary bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-xs",
        ghost: "",
        "ghost-primary": "text-primary",
        "ghost-destructive": "text-destructive",
        link: "text-text-primary underline-offset-4 hover:underline",
        "primary-accent":
          "bg-primary-accent/30 text-primary shadow-xs hover:bg-primary-accent/40",
        "la-salle-green":
          "bg-la-salle-green text-white shadow-xs hover:bg-la-salle-green/90",
        "butterfly-blue":
          "bg-butterfly-blue text-white shadow-xs hover:bg-butterfly-blue/90",
        hero:
          "!h-20 !rounded-[16px] !bg-[#1CACE0] !px-12 !text-[32px] !leading-none font-normal text-white shadow-none hover:!bg-[#7BDCFF] active:!bg-[#124792] disabled:!bg-[#1CACE0]/25 disabled:opacity-100",
        primary:
          "bg-primary font-medium rounded-sm w-36 shadow-xs hover:bg-primary/90",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-4 text-xs px-2 has-[>svg]:px-1.5",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-[50px] px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
      shape: {
        rounded: "rounded-full",
        square: "rounded-[4px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export type ButtonProps = React.ComponentPropsWithoutRef<"button"> &
  Omit<VariantProps<typeof buttonVariants>, "variant"> & {
    asChild?: boolean;
    variant?: ButtonVariants;
    loading?: boolean;
    icon?: string;
    iconSize?: ImageProps["size"];
    shape?: "rounded" | "square";
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      shape = "square",
      loading,
      icon,
      iconSize = "xxs",
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className, shape }))}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" />}
        {icon && <Image src={icon} size={iconSize} />}
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
